// src/pages/Extras/AddListingPage.jsx
// Enhanced with advanced validations and formatting

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAgent } from "../../Context/AgentContext";

const AddListingPage = () => {
  const navigate = useNavigate();
  const { agent, addListing, loading, error, clearError } = useAgent();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    Price: '',
    Contact_Info: '',
    Area_of_House: '',
    Area_of_Land: '',
    Address: '',
    Prefecture: '',
    Municipality: '',
    Year_of_Construction: '',
    Empty_or_Not: 'Sale',
    Room_Type: '',
    No_of_Floors: '',
    Link_to_Website: '',
    description: '',
    coordinates: '',
    latitude: '',
    longitude: ''
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [prefectureSearch, setPrefectureSearch] = useState('');
  const [showPrefectureDropdown, setShowPrefectureDropdown] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [areaUnit, setAreaUnit] = useState('m2'); // m2 or tsubo
  const [isAnimating, setIsAnimating] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showTranslationNote, setShowTranslationNote] = useState(true);

  const totalSteps = 4;

  // Japanese Prefectures
  const prefectures = [
    'Aichi', 'Akita', 'Aomori', 'Chiba', 'Ehime', 'Fukui', 'Fukuoka', 'Fukushima',
    'Gifu', 'Gunma', 'Hiroshima', 'Hokkaido', 'Hyogo', 'Ibaraki', 'Ishikawa',
    'Iwate', 'Kagawa', 'Kagoshima', 'Kanagawa', 'Kochi', 'Kumamoto', 'Kyoto',
    'Mie', 'Miyagi', 'Miyazaki', 'Nagano', 'Nagasaki', 'Nara', 'Niigata',
    'Oita', 'Okayama', 'Okinawa', 'Osaka', 'Saga', 'Saitama', 'Shiga',
    'Shimane', 'Shizuoka', 'Tochigi', 'Tokushima', 'Tokyo', 'Tottori',
    'Toyama', 'Wakayama', 'Yamagata', 'Yamaguchi', 'Yamanashi'
  ];

  // Japanese Room Types
  const roomTypes = [
    '1R', '1K', '1DK', '1LDK',
    '2K', '2DK', '2LDK',
    '3K', '3DK', '3LDK',
    '4K', '4DK', '4LDK',
    '5K', '5DK', '5LDK',
    '6LDK+', 'Studio', 'Maisonette'
  ];

  const filteredPrefectures = prefectures.filter(prefecture =>
    prefecture.toLowerCase().includes(prefectureSearch.toLowerCase())
  );

  // Format price with commas
  const formatPrice = (value) => {
    const numericValue = value.replace(/[^\d]/g, '');
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Format area with decimals
  const formatArea = (value) => {
    const cleanValue = value.replace(/[^\d.]/g, '');
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    return cleanValue;
  };

  // Convert tsubo to m2
  const tsuboToM2 = (tsubo) => {
    return parseFloat(tsubo) * 3.3058;
  };

  // Validate Japanese coordinates
  const validateJapaneseCoordinates = (lat, lng) => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    
    // Japan's approximate coordinate bounds
    const minLat = 24.0, maxLat = 46.0;
    const minLng = 122.0, maxLng = 146.0;
    
    return (
      latitude >= minLat && latitude <= maxLat &&
      longitude >= minLng && longitude <= maxLng
    );
  };

  // Validate year
  const validateYear = (year) => {
    const currentYear = new Date().getFullYear();
    const numYear = parseInt(year);
    return numYear >= 1800 && numYear <= currentYear;
  };

  const handleImageUpload = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      return isImage && isValidSize;
    });

    if (validFiles.length !== fileArray.length) {
      setErrors({...errors, images: 'Some files were rejected. Only images under 10MB are allowed.'});
    } else {
      setErrors({...errors, images: null});
    }

    setImageFiles(prev => [...prev, ...validFiles]);
  };

  const handlePdfUpload = (file) => {
    if (file && file.type === 'application/pdf') {
      if (file.size <= 10 * 1024 * 1024) { // 10MB limit
        setPdfFile(file);
        setErrors({...errors, pdf: null});
      } else {
        setErrors({...errors, pdf: 'PDF file must be under 10MB'});
      }
    } else {
      setErrors({...errors, pdf: 'Please select a valid PDF file'});
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    const images = files.filter(file => file.type.startsWith('image/'));
    const pdfs = files.filter(file => file.type === 'application/pdf');
    
    if (images.length > 0) {
      handleImageUpload(images);
    }
    if (pdfs.length > 0 && !pdfFile) {
      handlePdfUpload(pdfs[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const removeImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removePdf = () => {
    setPdfFile(null);
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;

    // Special handling for different field types
    switch (field) {
      case 'Price':
        processedValue = formatPrice(value);
        break;
      case 'Area_of_House':
      case 'Area_of_Land':
        processedValue = formatArea(value);
        break;
      case 'Year_of_Construction':
        processedValue = value.replace(/[^\d]/g, '').slice(0, 4);
        break;
      case 'latitude':
      case 'longitude':
        processedValue = value.replace(/[^\d.-]/g, '');
        // Update coordinates field
        const lat = field === 'latitude' ? processedValue : formData.latitude;
        const lng = field === 'longitude' ? processedValue : formData.longitude;
        setFormData(prev => ({
          ...prev,
          coordinates: `${lat},${lng}`.replace(/^,|,$/, '')
        }));
        break;
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Clear field-specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({...prev, [field]: null}));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1: // Basic Information
        if (!formData.Address.trim()) newErrors.Address = 'Address is required';
        if (!formData.Prefecture) newErrors.Prefecture = 'Prefecture is required';
        if (!formData.Municipality.trim()) newErrors.Municipality = 'Municipality is required';
        if (!formData.Price.trim()) {
          newErrors.Price = 'Price is required';
        } else if (!/^\d{1,3}(,\d{3})*$/.test(formData.Price)) {
          newErrors.Price = 'Please enter a valid price (numbers only)';
        }
        break;
      case 2: // Property Details
        if (!formData.Area_of_House.trim()) {
          newErrors.Area_of_House = 'House area is required';
        } else if (!/^\d+(\.\d+)?$/.test(formData.Area_of_House)) {
          newErrors.Area_of_House = 'Please enter a valid area (numbers only)';
        }
        if (!formData.Year_of_Construction.trim()) {
          newErrors.Year_of_Construction = 'Construction year is required';
        } else if (!validateYear(formData.Year_of_Construction)) {
          newErrors.Year_of_Construction = 'Please enter a valid year (1800-2024)';
        }
        if (!formData.Room_Type.trim()) newErrors.Room_Type = 'Room type is required';
        if (formData.Area_of_Land && !/^\d+(\.\d+)?$/.test(formData.Area_of_Land)) {
          newErrors.Area_of_Land = 'Please enter a valid land area (numbers only)';
        }
        break;
      case 3: // Additional Info
        if (!formData.Contact_Info.trim()) newErrors.Contact_Info = 'Contact information is required';
        if (!formData.description.trim()) newErrors.description = 'Property description is required';
        if (!formData.latitude.trim() || !formData.longitude.trim()) {
          newErrors.coordinates = 'Property coordinates are required';
        } else if (!validateJapaneseCoordinates(formData.latitude, formData.longitude)) {
          newErrors.coordinates = 'Please enter valid Japanese coordinates';
        }
        break;
      case 4: // Media Upload
        if (imageFiles.length === 0) newErrors.images = 'At least one image is required';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
        setIsAnimating(false);
      }, 300);
    }
  };

  const prevStep = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => Math.max(prev - 1, 1));
      setIsAnimating(false);
    }, 300);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setSubmitLoading(true);
    clearError();

    try {
      const formDataObj = new FormData();
      
      // Process areas - convert tsubo to m2 if needed
      let houseArea = formData.Area_of_House;
      let landArea = formData.Area_of_Land;
      
      if (areaUnit === 'tsubo') {
        houseArea = tsuboToM2(houseArea).toFixed(2);
        if (landArea) {
          landArea = tsuboToM2(landArea).toFixed(2);
        }
      }

      // Process price - remove commas
      const price = formData.Price.replace(/,/g, '');

      // Add all form fields
      const processedData = {
        ...formData,
        Price: price,
        Area_of_House: houseArea,
        Area_of_Land: landArea,
        coordinates: `${formData.latitude},${formData.longitude}`.replace(/^,|,$/, '')
      };

      Object.keys(processedData).forEach(key => {
        if (processedData[key] !== null && processedData[key] !== undefined && processedData[key] !== '') {
          formDataObj.append(key, processedData[key]);
        }
      });

      // Add agent info
      formDataObj.append('agent_id', agent.id);
      formDataObj.append('agent_name', agent.name);

      // Add images
      imageFiles.forEach((file, index) => {
        formDataObj.append('images', file);
      });

      // Add PDF if exists
      if (pdfFile) {
        formDataObj.append('pdf', pdfFile);
      }

      await addListing(formDataObj);
      
      // Show success modal
      setShowSuccessModal(true);
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/agent/my-listings');
      }, 3000);
      
    } catch (error) {
      console.error('Failed to add listing:', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderStepContent = () => {
    const stepClass = `transition-all duration-300 ${
      isAnimating ? 'opacity-0 transform translate-x-4' : 'opacity-100 transform translate-x-0'
    }`;

    switch (currentStep) {
      case 1:
        return (
          <div className={`space-y-6 ${stepClass}`}>
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Basic Information</h3>
            
            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Address *
              </label>
              <textarea
                value={formData.Address}
                onChange={(e) => handleInputChange('Address', e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                  errors.Address ? 'border-red-500' : 'border-gray-300'
                }`}
                rows="3"
                placeholder="Enter the complete property address"
              />
              {errors.Address && <p className="text-red-500 text-sm mt-1">{errors.Address}</p>}
            </div>

            {/* Prefecture */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prefecture *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={prefectureSearch || formData.Prefecture}
                  onChange={(e) => {
                    setPrefectureSearch(e.target.value);
                    setShowPrefectureDropdown(true);
                    handleInputChange('Prefecture', '');
                  }}
                  onFocus={() => setShowPrefectureDropdown(true)}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                    errors.Prefecture ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Search and select prefecture"
                />
                {showPrefectureDropdown && (
                  <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
                    {filteredPrefectures.map((prefecture) => (
                      <div
                        key={prefecture}
                        onClick={() => {
                          handleInputChange('Prefecture', prefecture);
                          setPrefectureSearch('');
                          setShowPrefectureDropdown(false);
                        }}
                        className="p-3 hover:bg-[var(--medium-sea-green)] hover:text-white cursor-pointer transition-colors"
                      >
                        {prefecture}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {errors.Prefecture && <p className="text-red-500 text-sm mt-1">{errors.Prefecture}</p>}
            </div>

            {/* Municipality */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Municipality/City *
              </label>
              <input
                type="text"
                value={formData.Municipality}
                onChange={(e) => handleInputChange('Municipality', e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                  errors.Municipality ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter municipality or city name"
              />
              {errors.Municipality && <p className="text-red-500 text-sm mt-1">{errors.Municipality}</p>}
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.Price}
                  onChange={(e) => handleInputChange('Price', e.target.value)}
                  className={`w-full p-3 pr-12 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                    errors.Price ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="5,000,000"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                  ¥
                </div>
              </div>
              {errors.Price && <p className="text-red-500 text-sm mt-1">{errors.Price}</p>}
            </div>
          </div>
        );

      case 2:
        return (
          <div className={`space-y-6 ${stepClass}`}>
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Property Details</h3>
            
            {/* Area Unit Toggle */}
            <div className="flex items-center space-x-4 mb-4">
              <span className="text-sm text-gray-600">Area Unit:</span>
              <button
                type="button"
                onClick={() => setAreaUnit('m2')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  areaUnit === 'm2' 
                    ? 'bg-[var(--medium-sea-green)] text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                m²
              </button>
              <button
                type="button"
                onClick={() => setAreaUnit('tsubo')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  areaUnit === 'tsubo' 
                    ? 'bg-[var(--medium-sea-green)] text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                坪
              </button>
            </div>

            {/* Area of House */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                House Area *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.Area_of_House}
                  onChange={(e) => handleInputChange('Area_of_House', e.target.value)}
                  className={`w-full p-3 pr-16 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                    errors.Area_of_House ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="120"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                  {areaUnit === 'm2' ? 'm²' : '坪'}
                </div>
              </div>
              {errors.Area_of_House && <p className="text-red-500 text-sm mt-1">{errors.Area_of_House}</p>}
            </div>

            {/* Area of Land */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Land Area
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.Area_of_Land}
                  onChange={(e) => handleInputChange('Area_of_Land', e.target.value)}
                  className={`w-full p-3 pr-16 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                    errors.Area_of_Land ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="200"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                  {areaUnit === 'm2' ? 'm²' : '坪'}
                </div>
              </div>
              {errors.Area_of_Land && <p className="text-red-500 text-sm mt-1">{errors.Area_of_Land}</p>}
            </div>

            {/* Year of Construction */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year of Construction *
              </label>
              <input
                type="text"
                value={formData.Year_of_Construction}
                onChange={(e) => handleInputChange('Year_of_Construction', e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                  errors.Year_of_Construction ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="1995"
                maxLength="4"
              />
              {errors.Year_of_Construction && <p className="text-red-500 text-sm mt-1">{errors.Year_of_Construction}</p>}
            </div>

            {/* Room Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Room Configuration *
              </label>
              <select
                value={formData.Room_Type}
                onChange={(e) => handleInputChange('Room_Type', e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                  errors.Room_Type ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select room type</option>
                {roomTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {errors.Room_Type && <p className="text-red-500 text-sm mt-1">{errors.Room_Type}</p>}
            </div>

            {/* Number of Floors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Floors
              </label>
              <select
                value={formData.No_of_Floors}
                onChange={(e) => handleInputChange('No_of_Floors', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all"
              >
                <option value="">Select floors</option>
                <option value="1">1 Floor</option>
                <option value="2">2 Floors</option>
                <option value="3">3 Floors</option>
                <option value="4">4 Floors</option>
                <option value="5+">5+ Floors</option>
              </select>
            </div>

            {/* Property Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Listing Type
              </label>
              <select
                value={formData.Empty_or_Not}
                onChange={(e) => handleInputChange('Empty_or_Not', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all"
              >
                <option value="Sale">For Sale</option>
                <option value="Rent">For Rent</option>
                <option value="Both">Sale or Rent</option>
              </select>
            </div>
          </div>
        );

      case 3:
        return (
          <div className={`space-y-6 ${stepClass}`}>
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Additional Information</h3>
            
            {/* Contact Information */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Information *
              </label>
              <textarea
                value={formData.Contact_Info}
                onChange={(e) => handleInputChange('Contact_Info', e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                  errors.Contact_Info ? 'border-red-500' : 'border-gray-300'
                }`}
                rows="3"
                placeholder="Phone number, email, or preferred contact method"
              />
              {errors.Contact_Info && <p className="text-red-500 text-sm mt-1">{errors.Contact_Info}</p>}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                rows="5"
                placeholder="Describe the property, its features, condition, and any other relevant details..."
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>

            {/* Website Link */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website/Listing Link (Optional)
              </label>
              <input
                type="url"
                value={formData.Link_to_Website}
                onChange={(e) => handleInputChange('Link_to_Website', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all"
                placeholder="https://example.com"
              />
            </div>

            {/* Coordinates */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Coordinates *
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Latitude *</label>
                  <input
                    type="text"
                    value={formData.latitude}
                    onChange={(e) => handleInputChange('latitude', e.target.value)}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                      errors.coordinates ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="35.6762"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Longitude *</label>
                  <input
                    type="text"
                    value={formData.longitude}
                    onChange={(e) => handleInputChange('longitude', e.target.value)}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[var(--medium-sea-green)] focus:border-transparent transition-all ${
                      errors.coordinates ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="139.6503"
                  />
                </div>
              </div>
              {errors.coordinates && <p className="text-red-500 text-sm mt-1">{errors.coordinates}</p>}
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  <strong>How to get coordinates from Google Maps:</strong><br/>
                  1. Open Google Maps and search for your property<br/>
                  2. Right-click on the exact location<br/>
                  3. Click on the coordinates that appear (e.g., "35.6762, 139.6503")<br/>
                  4. Copy the latitude (first number) and longitude (second number) separately
                </p>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className={`space-y-6 ${stepClass}`}>
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Media Upload</h3>
            
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Images * (Required)
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300 ${
                  dragActive ? 'border-[var(--medium-sea-green)] bg-[var(--medium-sea-green)]/5 transform scale-105' : 'border-gray-300'
                } ${errors.images ? 'border-red-500' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <i className="fas fa-cloud-upload-alt text-4xl text-gray-400 mb-4"></i>
                <p className="text-gray-600 mb-2">Drag and drop images here, or click to select</p>
                <p className="text-sm text-gray-500">Maximum 10MB per image</p>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files)}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="mt-4 inline-block px-6 py-2 bg-[var(--medium-sea-green)] text-white rounded-lg hover:bg-[var(--sea-green)] cursor-pointer transition-all duration-300 transform hover:scale-105"
                >
                  Select Images
                </label>
              </div>
              {errors.images && <p className="text-red-500 text-sm mt-1">{errors.images}</p>}
              
              {/* Display uploaded images */}
              {imageFiles.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                  {imageFiles.map((file, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-red-600 transform hover:scale-110"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* PDF Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Documents (PDF) - Optional
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center transition-all duration-300 hover:border-[var(--medium-sea-green)]">
                <i className="fas fa-file-pdf text-3xl text-gray-400 mb-2"></i>
                <p className="text-gray-600 mb-2">Upload property documents (Floor plans, certificates, etc.)</p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handlePdfUpload(e.target.files[0])}
                  className="hidden"
                  id="pdf-upload"
                />
                <label
                  htmlFor="pdf-upload"
                  className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer transition-all duration-300 transform hover:scale-105"
                >
                  Select PDF
                </label>
              </div>
              {errors.pdf && <p className="text-red-500 text-sm mt-1">{errors.pdf}</p>}
              
              {/* Display uploaded PDF */}
              {pdfFile && (
                <div className="mt-4 flex items-center justify-between bg-gray-50 p-3 rounded-lg transition-all duration-300">
                  <div className="flex items-center">
                    <i className="fas fa-file-pdf text-red-500 mr-2"></i>
                    <span className="text-sm text-gray-700">{pdfFile.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={removePdf}
                    className="text-red-500 hover:text-red-700 transition-colors duration-300 transform hover:scale-110"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* FontAwesome CDN */}
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
      />

      {/* Custom Styles */}
      <style>{`
        .teal-border {
          border-color: rgba(14, 177, 169, 0.2);
        }
        .teal-bg {
          background-color: rgba(14, 177, 169, 0.05);
        }
        .teal-hover:hover {
          background-color: rgba(14, 177, 169, 0.1);
        }
        .border-primary {
          border-color: rgba(16, 201, 192, 0.2);
        }
        .f-section {
          max-width: 1200px;
        }
        .dark-header-nospacing {
          font-size: 1.5rem;
          font-weight: 700;
        }
        .non-selectable {
          user-select: none;
        }
      `}</style>

      {/* Header */}
      <header className="bg-[var(--medium-sea-green)] shadow-sm border-b teal-border">
        <div className="f-section mx-auto" style={{ padding: '0 clamp(6px, 4vw, 30px)' }}>
          {/* Top Header Bar */}
          <div className="h-20 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/agent/dashboard')}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-colors"
              >
                <i className="fas fa-arrow-left text-white"></i>
              </button>
              <h1 className="dark-header-nospacing pl-2" style={{ color: 'white', margin: 0 }}>
                Add New Listing
              </h1>
            </div>

            {/* Agent Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-white/10 transition-colors non-selectable"
              >
                <div className="text-right">
                  <p className="text-sm font-medium text-white">
                    {agent?.name || 'Agent'}
                  </p>
                  <p className="text-xs text-white opacity-70">
                    Real Estate Agent
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/20">
                  {agent?.profile_image || agent?.image_url ? (
                    <img
                      src={agent.profile_image || agent.image_url}
                      alt="Profile"
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <i className="fas fa-user text-white"></i>
                  )}
                </div>
                <i className="fas fa-chevron-down text-white text-sm opacity-70"></i>
              </button>

              {/* Dropdown Menu */}
              {showProfileDropdown && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border-primary border py-2 z-50">
                  <button
                    onClick={() => {
                      navigate('/agent/profile');
                      setShowProfileDropdown(false);
                    }}
                    className="flex items-center w-full teal-hover transition-colors text-left non-selectable"
                    style={{ padding: '8px clamp(6px, 4vw, 16px)', color: 'var(--color-2)' }}
                  >
                    <i className="fas fa-user mr-3 w-4"></i>
                    Profile Settings
                  </button>
                  <button
                    onClick={() => {
                      navigate('/agent/dashboard');
                      setShowProfileDropdown(false);
                    }}
                    className="flex items-center w-full teal-hover transition-colors text-left non-selectable"
                    style={{ padding: '8px clamp(6px, 4vw, 16px)', color: 'var(--color-2)' }}
                  >
                    <i className="fas fa-tachometer-alt mr-3 w-4"></i>
                    Dashboard
                  </button>
                  <button
                    onClick={() => {
                      navigate('/agent/my-listings');
                      setShowProfileDropdown(false);
                    }}
                    className="flex items-center w-full teal-hover transition-colors text-left non-selectable"
                    style={{ padding: '8px clamp(6px, 4vw, 16px)', color: 'var(--color-2)' }}
                  >
                    <i className="fas fa-list mr-3 w-4"></i>
                    My Listings
                  </button>
                  {agent?.isSuperAdmin && (
                    <>
                      <div className="border-t border-primary my-1"></div>
                      <button
                        onClick={() => {
                          navigate('/super-admin');
                          setShowProfileDropdown(false);
                        }}
                        className="flex items-center w-full teal-hover transition-colors text-left non-selectable"
                        style={{ padding: '8px clamp(6px, 4vw, 16px)', color: 'var(--color-2)' }}
                      >
                        <i className="fas fa-shield-alt mr-3 w-4"></i>
                        Super Admin Panel
                      </button>
                    </>
                  )}
                  <div className="border-t border-primary my-1"></div>
                  <button
                    onClick={() => {
                      localStorage.removeItem('access_token');
                      navigate('/');
                    }}
                    className="flex items-center w-full teal-hover transition-colors text-left non-selectable"
                    style={{ padding: '8px clamp(6px, 4vw, 16px)', color: 'var(--color-2)' }}
                  >
                    <i className="fas fa-sign-out-alt mr-3 w-4"></i>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Translation Notice */}
      {showTranslationNote && (
        <div className="bg-[var(--sea-green)]/10 border-l-4 border-[var(--sea-green)]">
          <div className="f-section mx-auto" style={{ padding: '16px clamp(6px, 4vw, 30px)' }}>
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <i className="fas fa-language text-[var(--sea-green)] mt-0.5 mr-3"></i>
                <div>
                  <p className="text-sm" style={{ color: 'var(--color-2)' }}>
                    <strong>Translation Support:</strong> You can enter data in Japanese, which will be automatically translated by our system. 
                    For the best translation quality and to avoid depending on auto-translation, please contact us at{' '}
                    <a 
                      href="mailto:info@akiya2.com" 
                      className="text-[var(--sea-green)] hover:opacity-70 underline font-medium"
                    >
                      info@akiya2.com
                    </a>
                  </p>
                </div>
              </div>
              {/* <button
                onClick={() => setShowTranslationNote(false)}
                className="text-[var(--sea-green)] hover:opacity-70 ml-4 flex-shrink-0"
              >
                <i className="fas fa-times"></i>
              </button> */}
            </div>
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center">
            {[1, 2, 3, 4].map((step, index) => (
              <React.Fragment key={step}>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                    step === currentStep 
                      ? 'bg-[var(--medium-sea-green)] text-white transform scale-110' 
                      : step < currentStep 
                        ? 'bg-[var(--sea-green)] text-white' 
                        : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step < currentStep ? (
                      <i className="fas fa-check"></i>
                    ) : (
                      step
                    )}
                  </div>
                  <span className={`ml-2 text-sm font-medium transition-all duration-300 ${
                    step === currentStep ? 'text-[var(--medium-sea-green)]' : step < currentStep ? 'text-[var(--sea-green)]' : 'text-gray-500'
                  }`}>
                    {step === 1 && 'Basic Info'}
                    {step === 2 && 'Property Details'}
                    {step === 3 && 'Additional Info'}
                    {step === 4 && 'Media Upload'}
                  </span>
                </div>
                {index < 3 && (
                  <div className={`w-12 h-0.5 mx-4 transition-all duration-500 ${
                    step < currentStep ? 'bg-[var(--sea-green)]' : 'bg-gray-200'
                  }`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between transition-all duration-300 transform">
            <div className="flex items-center">
              <i className="fas fa-exclamation-triangle text-red-500 mr-2"></i>
              <span className="text-red-700">{error}</span>
            </div>
            <button 
              onClick={clearError}
              className="text-red-500 hover:text-red-700 transition-colors duration-300 transform hover:scale-110"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 transition-all duration-300">
          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 transform hover:scale-105'
              }`}
            >
              <i className="fas fa-chevron-left mr-2"></i>
              Previous
            </button>

            {currentStep < totalSteps ? (
              <button
                onClick={nextStep}
                className="px-6 py-3 bg-[var(--medium-sea-green)] text-white rounded-lg font-medium hover:bg-[var(--sea-green)] transition-all duration-300 transform hover:scale-105"
              >
                Next
                <i className="fas fa-chevron-right ml-2"></i>
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitLoading || loading}
                className={`px-8 py-3 rounded-lg font-medium transition-all duration-300 ${
                  submitLoading || loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[var(--medium-sea-green)] text-white hover:bg-[var(--sea-green)] shadow-lg transform hover:scale-105'
                }`}
              >
                {submitLoading || loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check mr-2"></i>
                    Submit Listing
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 transform animate-pulse">
            <div className="text-center">
              <div className="w-16 h-16 bg-[var(--medium-sea-green)] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-check text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Success!</h3>
              <p className="text-gray-600 mb-4">
                Your property listing has been submitted successfully!
              </p>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <i className="fas fa-spinner fa-spin text-[var(--medium-sea-green)]"></i>
                <span>Redirecting to your listings...</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddListingPage;